import React from 'react';
import { trackPricingInteraction, trackButtonClick } from '../utils/analytics';

const Pricing = () => {
  const plans = [
    {
      name: "Free",
      price: "Coming Soon",
      period: "",
      description: "Perfect for getting started with basic cycle tracking",
      features: [
        "Basic cycle tracking and predictions",
        "Symptom logging",
        "Educational content library",
        "Community access",
        "Basic product recommendations"
      ],
      cta: "Get Started Free",
      popular: false
    },
    {
      name: "Premium",
      price: "Coming Soon",
      period: "",
      description: "Advanced features for comprehensive wellness",
      features: [
        "Everything in Free",
        "AI-powered personalized recommendations",
        "Advanced cycle analytics",
        "Expert Q&A access",
        "Premium product partnerships",
        "Custom wellness plans",
        "Priority customer support",
        "Data export & backup"
      ],
      cta: "Join Waitlist",
      popular: true
    }
  ];

  const productPartnerships = [
    {
      name: "Organic Pads & Tampons",
      discount: "15% off",
      description: "Eco-friendly, hypoallergenic products"
    },
    {
      name: "Menstrual Cups",
      discount: "20% off",
      description: "Reusable, sustainable options"
    },
    {
      name: "Wellness Supplements",
      discount: "10% off",
      description: "Cycle-supporting vitamins and minerals"
    },
    {
      name: "Fitness & Wellness",
      discount: "25% off",
      description: "Yoga, meditation, and fitness programs"
    }
  ];

  return (
    <section id="pricing" className="section-padding bg-white">
      <div className="container-max">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Start
            <span className="gradient-text"> Free</span>
            , Upgrade Later
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Start with our free plan and unlock premium features when you're ready. 
            Premium members will get exclusive access to product partnerships and expert guidance.
          </p>
        </div>

        {/* Pricing Plans */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16 max-w-4xl mx-auto">
          {plans.map((plan, index) => (
            <div key={index} className={`card p-8 relative ${plan.popular ? 'ring-2 ring-primary-500' : ''}`}>
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-primary-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                    Most Popular
                  </span>
                </div>
              )}
              
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                <div className="mb-2">
                  <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                  {plan.period && <span className="text-gray-500 ml-1">/{plan.period}</span>}
                </div>
                <p className="text-gray-600">{plan.description}</p>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start">
                    <svg className="w-5 h-5 text-primary-600 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              {plan.cta !== "Get Started Free" && (
                <button 
                  onClick={() => {
                    trackPricingInteraction(plan.name, 'Join Waitlist Click');
                    document.getElementById('waitlist').scrollIntoView({ behavior: 'smooth' });
                  }}
                  className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 ${
                    plan.popular 
                      ? 'btn-primary' 
                      : 'btn-secondary'
                  }`}>
                  {plan.cta}
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Product Partnerships */}
        <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-2xl p-8 lg:p-12">
          <div className="text-center mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Premium Product Partnerships
            </h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Premium members get exclusive discounts on carefully curated products 
              that support your wellness journey.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {productPartnerships.map((partnership, index) => (
              <div key={index} className="bg-white rounded-xl p-6 text-center shadow-sm hover:shadow-md transition-shadow duration-200">
                <svg className="w-12 h-12 text-primary-600 mx-auto mb-3" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12L8.1 13h7.45c.75 0 1.41-.41 1.75-1.03L21.7 4H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z"/>
                </svg>
                <h4 className="font-semibold text-gray-900 mb-1">{partnership.name}</h4>
                <div className="text-primary-600 font-bold text-lg mb-2">{partnership.discount}</div>
                <p className="text-gray-600 text-sm">{partnership.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="text-gray-600 text-sm">
              * Premium members receive affiliate commission on purchases made through our platform
            </p>
          </div>
        </div>

        {/* FAQ Preview */}
        <div className="text-center mt-16">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            Questions about our plans?
          </h3>
          <p className="text-gray-600 mb-6">
            Check out our FAQ section or contact us for more information.
          </p>
          <button 
            onClick={() => {
              trackButtonClick('View FAQ', 'Pricing Section');
              document.getElementById('faq').scrollIntoView({ behavior: 'smooth' });
            }}
            className="btn-secondary"
          >
            View FAQ
          </button>
        </div>
      </div>
    </section>
  );
};

export default Pricing; 